import { FormControl } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { iconHoverBorderStyle } from "ds_legacy/materials/layouts";
import { dp, margin, padding } from "ds_legacy/materials/metrics";
import { FONT_SIZE_14 } from "ds_legacy/materials/typography";
import { useEffect, useMemo, useState } from "react";
import { FormElement, FormElementProps, isValid } from "react-forms-state";
import Select, { PropsValue } from "react-select";
import { useTranslations } from "translations";
import { Validation } from "../Validation";
import { Control } from "./Control";
import { DropdownIndicator } from "./DropdownIndicator";
import { IndicatorsContainer } from "./IndicatorsContainer";
import { Menu } from "./Menu";
import { MultiValue as MultiValueComp } from "./MultiValue";
import { NoOptionsMessage } from "./NoOptionsMessage";
import { Option } from "./Option";
import { Placeholder } from "./Placeholder";
import { SingleValue as SingleValueComp } from "./SingleValue";
import { SelectOption, SelectType } from "./types";
import {
  getTextFieldProps,
  getTextFieldStyles,
  isValueEmpty,
  useStyles,
} from "./utils";
import { ValueContainer } from "./ValueContainer";

export default function ReactSelect({
  ariaLabel,
  cannotClear = false,
  closeMenuOnSelect = true,
  customCss,
  customNoResult,
  disabled,
  elementName,
  errorOverride,
  fontSize = FONT_SIZE_14,
  hideSelectedOptions = true,
  id,
  isLoading,
  isSearchable = true,
  label,
  maxWidth,
  minWidth,
  multiple = false,
  onBlur,
  onChange = (v: any) => v,
  onFocus,
  onMouseOut,
  onMouseOver,
  onSearch = (e: any) => e,
  options,
  placeholder = "",
  required,
  testId,
  useShortValue = false,
  validation,
  value = null,
  width = dp(250),
  withPortal = false,
}: SelectType) {
  const [localValue, setLocalValue] = useState<
    PropsValue<SelectOption> | undefined
  >(value);
  const theme = useTheme();
  const translations = useTranslations();
  const [searchInputValue, setSearchInputValue] = useState("");

  // #TODO FIXME: remove this once we have a proper solution for empty values
  // eslint-disable-next-line no-constant-binary-expression
  const empty = false && isValueEmpty(value, multiple);
  const hasError = errorOverride || isValid(validation) === false;
  const errorTextId = `${elementName}-error-text`;
  const textFieldStyles = getTextFieldStyles({
    fontSize,
    hasError,
  });
  const classes = useStyles({
    fontSize,
    hasError,
    isSearchable,
  })();

  const selectPlaceholder = useMemo((): string => {
    return placeholder !== ""
      ? placeholder
      : translations.actions.selectPlaceholder;
  }, [placeholder]);

  const textFieldProps = getTextFieldProps({
    customNoResult,
    elementName,
    empty,
    label,
    onBlur,
    onFocus,
    onSearch,
    placeholder,
    required,
    setSearchInputValue,
    testId,
    textFieldStyles,
  });

  useEffect(() => {
    let newValue = value;

    if (multiple && Array.isArray(value)) {
      setLocalValue(value);
    } else if (value && options.length) {
      newValue = options.find(
        (el) => el.label === value.label || el.value === value,
      );
      setLocalValue(newValue);
      // in algolia there are no "options" on convertIn
    } else if (value && !!value.label && !!value.value) {
      setLocalValue(newValue);
    }
  }, [value]);

  return (
    <FormControl
      className={classes?.root}
      data-testid={`select-wrapper-${elementName}`}
      style={{
        maxWidth,
        minWidth,
        width,
      }}
      onMouseEnter={onMouseOver}
      onMouseLeave={onMouseOut}
    >
      <Select
        {...{ ["aria-label"]: ariaLabel ?? label ?? "" }}
        aria-describedby={errorTextId}
        classes={classes}
        closeMenuOnSelect={closeMenuOnSelect}
        components={{
          Control,
          DropdownIndicator,
          IndicatorsContainer,
          IndicatorSeparator: () => null,
          Menu,
          MultiValue: MultiValueComp,
          NoOptionsMessage,
          Option,
          Placeholder,
          SingleValue: SingleValueComp,
          ValueContainer,
        }}
        customCss={customCss}
        customNoResult={customNoResult}
        hasError={hasError}
        hideSelectedOptions={hideSelectedOptions}
        inputId={id}
        isClearable={!cannotClear}
        isDisabled={disabled}
        isLoading={isLoading}
        isMulti={multiple}
        isSearchable={isSearchable}
        menuPortalTarget={withPortal ? document.body : null}
        options={options?.length ? options : []}
        placeholder={selectPlaceholder}
        searchInputValue={searchInputValue}
        styles={{
          clearIndicator: (styles: any) => ({
            ...styles,
            ":hover": {
              ...iconHoverBorderStyle(theme),
              cursor: "pointer",
            },
            borderRadius: dp(24),
            margin: margin(0),
            padding: padding(0),
          }),
          menuPortal: (styles: any) => ({ ...styles, zIndex: 1000000000000 }),
        }}
        textFieldProps={textFieldProps}
        useShortValue={useShortValue}
        value={localValue}
        width={width}
        onChange={(newValue: any) => {
          if (newValue === null) {
            setLocalValue(null);
            onChange?.(null);
            return;
          }
          setLocalValue(newValue as SelectOption);
          onChange?.(multiple ? { value: newValue } : newValue);
          if (customNoResult) setSearchInputValue("");
        }}
      />
      <Validation
        elementName={elementName || ""}
        formHelperTextSx={{ marginLeft: 0 }}
        hasCustomValidation
        id={errorTextId}
        validation={validation}
      />
    </FormControl>
  );
}

export const SelectConnectedWithForm = FormElement()<
  FormElementProps & SelectType
>(ReactSelect);
